import {
    Component,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Input,
    OnInit,
    Self,
    input,
} from '@angular/core';
import { FormGroup, FormArray, NgControl, FormControl, Validators } from '@angular/forms';
import {
    API_DATETIME_FORMAT,
    BazisConfigurationService,
    COMMON_DATE_MASK_SETTINGS,
} from '@bazis/configuration.service';
import { BazisControlValueAccessor } from '@bazis/form/components/control-value-accessor.component';
import { ScheduleItemData } from '@bazis/form/models/form-element.types';
import { EntityFormControl } from '@bazis/form/models/form.types';
import { BazisFormService } from '@bazis/form/services/form.service';
import { DateValidators } from '@bazis/form/validators/dateValidators';
import { TranslocoService } from '@jsverse/transloco';
import { UntilDestroy } from '@ngneat/until-destroy';
import moment from 'moment';
import { debounceTime, of, tap } from 'rxjs';

@UntilDestroy()
@Component({
    selector: 'app-input-schedule',
    templateUrl: './input-schedule.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class AppInputScheduleComponent extends BazisControlValueAccessor implements OnInit {
    @Input()
    addButtonTitleKey = '';

    readonly anchorRoot = input<string>('');

    dateFormat: string = API_DATETIME_FORMAT;

    disabled = false;

    scheduleForm = new FormGroup({ schedule: new FormArray([]) });

    schedule = this.scheduleForm.controls.schedule as FormArray;

    daysOfWeek = ['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'].map((day) => {
        return {
            id: day,
            nameKey: `weekDays.short.${day}`,
        };
    });

    timeIntervalMaskSettings = {
        mask: `00:00 - 00:00`,
        ...COMMON_DATE_MASK_SETTINGS,
    };

    previousSchedule = []; // to keep previous schedule value

    daysRequired$ = of(true);

    workingHoursRequired$ = of(true);

    valueChanges$ = this.scheduleForm.valueChanges.pipe(
        debounceTime(500),
        tap((value) => {
            const schedule = {};
            // GOAL: if we add day of week that exists in another schedule row, we must remove it from old row

            // detect index of row that was changed
            const changedIndex = value.schedule.findIndex(
                (v, index) =>
                    v &&
                    this.previousSchedule[index] &&
                    JSON.stringify(v) !== JSON.stringify(this.previousSchedule[index]),
            );
            const changedItem = value.schedule[changedIndex];

            // if we have changedItem and selected days inside it,
            // we must check another items and remove these days from their values.
            if (changedIndex > -1 && changedItem.days.length > 0) {
                value.schedule.forEach((scheduleItem, index) => {
                    if (index === changedIndex) return;
                    const days = scheduleItem.days.reduce((acc, day) => {
                        if (changedItem.days.indexOf(day) === -1) {
                            acc.push(day);
                        }
                        return acc;
                    }, []);

                    if (days.length !== scheduleItem.days.length) {
                        (this.schedule.at(index) as FormGroup).controls.days.setValue(days);
                    }
                });
            }

            // save current value as previous - full copy
            this.previousSchedule = JSON.parse(JSON.stringify(this.schedule.value));

            // generate new schedule to save it in api
            value.schedule.forEach((scheduleItem) => {
                const time = {
                    rest: {
                        to: this.ISOtoMinutes(this.timeToDate(scheduleItem.restHours.slice(8, 13))),
                        from: this.ISOtoMinutes(
                            this.timeToDate(scheduleItem.restHours.slice(0, 5)),
                        ),
                    },
                    work: {
                        to: this.ISOtoMinutes(
                            this.timeToDate(scheduleItem.workingHours.slice(8, 13)),
                        ),
                        from: this.ISOtoMinutes(
                            this.timeToDate(scheduleItem.workingHours.slice(0, 5)),
                        ),
                    },
                };
                scheduleItem.days.forEach((day) => {
                    schedule[day] = time;
                });
            });
            this.onChange(Object.keys(schedule).length ? schedule : null);
        }),
    );

    constructor(
        @Self() public ngControl: NgControl,
        protected cdr: ChangeDetectorRef,
        configurationService: BazisConfigurationService,
        private translateService: TranslocoService,
    ) {
        super(ngControl, cdr, configurationService);
        ngControl.valueAccessor = this;
    }

    ngOnInit() {
        if (this.ngControl.control instanceof EntityFormControl) {
            BazisFormService.getPropertiesFromConfig(this, this.ngControl.control.$config);
            this.ngControl.control.$config.componentValidatorFns = [this.validate.bind(this)];
        }
        this.ngControl.control.setValidators([this.validate.bind(this)]);
        this.ngControl.control.updateValueAndValidity();

        this.scheduleForm.updateValueAndValidity();
    }

    validate({ value }: FormControl) {
        let componentError;

        this.scheduleForm.controls.schedule.controls.forEach((scheduleLine, index) => {
            if (scheduleLine.errors?.scheduleDaysEmpty) {
                componentError = {
                    requiredFieldId: this.anchorRoot + '-days-' + index,
                    titleKey: 'form.title.schedule.day',
                };
            }
            if (scheduleLine.errors?.scheduleWorkingHoursEmpty) {
                componentError = {
                    requiredFieldId: this.anchorRoot + '-workingHours-' + index,
                    titleKey: 'form.title.schedule.workingHours',
                };
            }
            // if (scheduleLine.errors?.scheduleRestHoursEmpty) {
            //     componentError = {
            //         requiredFieldId: this.anchorRoot + '-restHours-' + index,
            //         titleKey: 'form.title.schedule.restInterval',
            //     };
            // }
        });

        return this.scheduleForm.status === 'VALID'
            ? null
            : { componentError: componentError ? componentError : true };
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.scheduleForm.disable();
        } else {
            this.scheduleForm.enable();
        }
    }

    writeValue(value): void {
        this.schedule.clear({ emitEvent: false });

        if (!value || !Object.keys(value).length) {
            this.init();
            return;
        }
        const groups = [];
        Object.keys(value).forEach((day) => {
            const group = groups.find(
                (existedGroup) => JSON.stringify(existedGroup.time) === JSON.stringify(value[day]),
            );
            if (group) {
                group.days.push(day);
            } else {
                groups.push({
                    days: [day],
                    time: value[day],
                });
            }
        });

        groups.forEach((groupItem) => {
            this.schedule.push(this.generateScheduleItem(groupItem.time, groupItem.days));
        });
        this.previousSchedule = JSON.parse(JSON.stringify(this.schedule.value));
    }

    init() {
        this.schedule.push(this.generateScheduleItem());
        setTimeout(() => {
            this.scheduleForm.updateValueAndValidity();
        });
    }

    generateScheduleItem(data: ScheduleItemData = null, days = null) {
        return new FormGroup(
            {
                days: new FormControl(days || [], [Validators.required]),
                workingHours: new FormControl(this.numberIntervalToStringInterval(data?.work), [
                    DateValidators.intervalTimeValidatorFn(' - '),
                    Validators.required,
                ]),
                restHours: new FormControl(
                    this.numberIntervalToStringInterval(data?.rest),
                    DateValidators.intervalTimeValidatorFn(' - '),
                ),
            },
            [this.validateScheduleItem],
        );
    }

    validateScheduleItem(item) {
        const err = { wrongScheduleRow: true };

        if (!item.controls.days.value.length) return { scheduleDaysEmpty: true };
        if (!item.controls.workingHours.value) return { scheduleWorkingHoursEmpty: true };
        // if (!item.controls.restHours.value) return { scheduleRestHoursEmpty: true };

        if (
            item.controls.days.invalid ||
            item.controls.workingHours.invalid ||
            item.controls.restHours.invalid
        ) {
            return err;
        }

        // force to the same year, month, date
        const rest = item.value.restHours;
        const work = item.value.workingHours;

        const workFrom = moment
            .utc(`1970-01-01T${work.slice(0, 5)}`)
            .year(1970)
            .month(0)
            .date(1);
        const workTo = moment
            .utc(`1970-01-01T${work.slice(8, 13)}`)
            .year(1970)
            .month(0)
            .date(1);
        const restFrom = moment
            .utc(`1970-01-01T${rest.slice(0, 5)}`)
            .year(1970)
            .month(0)
            .date(1);
        const restTo = moment
            .utc(`1970-01-01T${rest.slice(8, 13)}`)
            .year(1970)
            .month(0)
            .date(1);

        return item.value.restHours.length === 13 && (restFrom < workFrom || restTo > workTo)
            ? err
            : null;
    }

    numberIntervalToStringInterval(date) {
        return date
            ? (date.from === 0 || date.from
                  ? moment('1970-01-01')
                        .hour(date.from / 60)
                        .minute(date.from % 60) // *60000 convert minutes to milliseconds
                        .format('HH:mm')
                  : '') +
                  ' - ' +
                  (date.to === 0 || date.to
                      ? moment('1970-01-01')
                            .hour(date.to / 60)
                            .minute(date.to % 60) // *60000 convert minutes to milliseconds
                            .format('HH:mm')
                      : '')
            : '';
    }

    timeToDate(time) {
        return time ? `1970-01-01T${time}` : '';
    }

    addScheduleGroup() {
        this.schedule.push(this.generateScheduleItem());
    }

    removeSchedule(i: number) {
        this.schedule.removeAt(i);
    }

    ISOtoMinutes(isoString) {
        if (!isoString) return null;

        const date = moment(isoString);
        if (!moment.utc(isoString).isValid()) return null;

        return date.hours() * 60 + date.minutes();
    }
}
